import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import {
	defaultTimeZone,
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	ROLE_TYPES,
} from '../../utils/DataConstants';
import {
	downloadWalkthroughsCSV,
	refreshWalkthroughCountsForBuyer,
	refreshWalkthroughCountsForSupplier,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { WALKTHROUGHS_CRUD_ACTION_CREATORS } from '../../actions/walkthroughs_actions';
import { WalkthroughRowDisplay } from '../walkthrough_row_display/WalkthroughRowDisplay';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Button, Col } from 'antd';
import {
	getStateSliceProp,
	getTimeZoneFieldName,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import ScheduleWalkthroughModalForm from '../schedule_walkthrough_modal_form/ScheduleWalkthroughModalForm';
import WalkthroughPipeLineFilters from '../site_surveys_detail_details_page/walkthroughs_pipeline_filters';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { DownloadOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import getWalkthroughSorters from './WalkthroughSorters';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import OWDateRangePicker from '../ow_date_range_picker/OWDateRangePicker';
import { filterValueAccessorDateTime, handleChangeDateTime } from '../../hooks/workOrderHooks';

const TC_NAME = 'siteSurveyAssociatedWalkthroughs';
const WALKTHROUGH_ADVANCED_FILTERS = 'walkthroughAdvancedFilters';

const WalkthroughsIndexPage: FC<any> = ({
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	refreshCounts,
	walkthroughs,
	location,
	history,
	userType,
	currentUser,
	fetchWalkthroughs,
	locations,
	fetchLocations,
	downloadWalkthroughCSV,
}): React.ReactElement => {
	const [scheduleVisible, setScheduleVisible] = useState(false);

	const _showSchedule = () => setScheduleVisible(true);
	const _hideSchedule = () => setScheduleVisible(false);

	const onScheduleSuccess = useCallback(() => {
		_hideSchedule();
		const filters = getStateSliceProp('filters', TC_NAME, walkthroughs);
		fetchWalkthroughs(
			{},
			TC_NAME,
			getStateSliceProp('pagination', TC_NAME, walkthroughs),
			getStateSliceProp('sorting', TC_NAME, walkthroughs),
			filters
		);
		refreshCounts(filters);
	}, [fetchWalkthroughs, refreshCounts, walkthroughs]);

	const downloadCSV = useCallback(() => {
		const filters = walkthroughs[TC_NAME].filters;
		downloadWalkthroughCSV({}, filters);
	}, [downloadWalkthroughCSV, walkthroughs]);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'title',
				render: (_, record) => <WalkthroughRowDisplay walkthrough={record} />,
			},
		],
		[]
	);

	const onWalkthroughsRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				const path = `/${userType}/siteSurveys/walkthrough/detail/${record.id}/details`;
				history.push(getLinkWIthBackLinkParams(location, 'Back to Walkthroughs', path));
			},
		}),
		[history, location, userType]
	);

	const isScheduleCTAVisible = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Location',
				fieldName: FILTER_CONFIG_NAMES.LOCATION,
				mode: 'multiple',
				elementDefined: true,
				stateSlice: locations,
				keyAccessor: (loc) => loc.id,
				valueAccessor: (loc) => loc.id,
				targetCollectionName: WALKTHROUGH_ADVANCED_FILTERS,
				renderItem: (loc) => loc.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: RegionsLocationsTreeSelect,
			},
			{
				label: 'Created Between',
				labelPrefix: 'Created',
				fieldName: FILTER_CONFIG_NAMES.CREATED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				filtersValueAccessor: (filtersStateSlice) =>
					filterValueAccessorDateTime(filtersStateSlice, FILTER_CONFIG_NAMES.CREATED_AT),
				handleChange: (values, timeZone = defaultTimeZone) =>
					handleChangeDateTime(updateFilters, FILTER_CONFIG_NAMES.CREATED_AT, values, timeZone),
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
				showTimeZone: true,
				additionalDefaultValues: [
					{
						[getTimeZoneFieldName(FILTER_CONFIG_NAMES.CREATED_AT)]: undefined,
					},
				],
			},
			{
				label: 'Completed Between',
				labelPrefix: 'Completed',
				fieldName: FILTER_CONFIG_NAMES.COMPLETED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				filtersValueAccessor: (filtersStateSlice) =>
					filterValueAccessorDateTime(filtersStateSlice, FILTER_CONFIG_NAMES.COMPLETED_AT),
				handleChange: (values, timeZone = defaultTimeZone) =>
					handleChangeDateTime(updateFilters, FILTER_CONFIG_NAMES.COMPLETED_AT, values, timeZone),
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
				showTimeZone: true,
				additionalDefaultValues: [
					{
						[getTimeZoneFieldName(FILTER_CONFIG_NAMES.COMPLETED_AT)]: undefined,
					},
				],
			},
		],
		[fetchLocations, locations]
	);

	const completedFilterEnabled = useMemo(() => {
		const filters = getStateSliceProp('filters', TC_NAME, walkthroughs);
		const statuesFilter = nullSafeGetOrElse('statuses', filters, '');
		return statuesFilter.split(',').includes('complete');
	}, [walkthroughs]);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={walkthroughs}
				refreshCounts={refreshCounts}
				sorters={getWalkthroughSorters(completedFilterEnabled)}
				initialSorters={{
					sort_by: 'updatedAt',
					order: 'desc',
				}}
				updateSorters={updateSorters}
				pipeLineFilters={WalkthroughPipeLineFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="walkthroughs"
				tableColumns={columns}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? walkthroughsRestCrudThunksForSupplier.read
						: walkthroughsRestCrudThunksForBuyer.read
				}
				rightActions={
					<Col style={{ textAlign: 'right' }}>
						<Button
							style={{ marginRight: 12 }}
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xxl">Export CSV</span>
						</Button>
						{isScheduleCTAVisible ? (
							<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
								<Button type="primary" size="large" onClick={_showSchedule}>
									Schedule Walkthrough
								</Button>
							</AccessPermissionChecker>
						) : null}
					</Col>
				}
				onTableRow={onWalkthroughsRow}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt="You haven't conducted any walkthroughs yet."
							/>
						}
						headline={"You haven't conducted any walkthroughs yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									{isScheduleCTAVisible ? (
										<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
											<Button type="primary" size="large" onClick={_showSchedule}>
												Schedule Walkthrough
											</Button>
										</AccessPermissionChecker>
									) : null}
								</div>
							</div>
						}
					/>
				}
			/>
			{scheduleVisible ? (
				<ScheduleWalkthroughModalForm onCancel={_hideSchedule} onSuccess={onScheduleSuccess} />
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	walkthroughs: state.walkthroughs,
	locations: state.locations,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshCounts: (filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? refreshWalkthroughCountsForSupplier(filters)
				: refreshWalkthroughCountsForBuyer(filters)
		),
	updateFilters: (filters, targetCollection) =>
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			WALKTHROUGHS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	fetchWalkthroughs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: walkthroughsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite(
						{ ...params, noIsActiveFilter: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: locationsRestCrudThunksForBuyer.readLite(
						{ ...params, noIsActiveFilter: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	downloadWalkthroughCSV: (params, filters) =>
		dispatch(downloadWalkthroughsCSV(ownProps.userType)(params, filters)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(WalkthroughsIndexPage as any)) as any
);
